import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useChatOps, UseChatOpsProps } from "../hooks/useChatOps";
import { ChatOpsFormSkeleton } from "../components/ChatOpsFormSkeleton";
import { useEffect } from "react";
import { ChatOpsFormBase } from "../components/ChatOpsFormBase";

interface TelegramFormValues {
  activeState: boolean;
  chatid: string;
  secret: string;
}

export function ChatOpsTelegramForm(props: UseChatOpsProps) {
  const { integration, saveIntegration, hasChatOpsPermission } =
    useChatOps(props);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<TelegramFormValues>({
    defaultValues: {
      activeState: false,
      chatid: "",
      secret: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (integration) {
      reset({
        activeState: integration.enabled ?? false,
        chatid: integration.options?.chatid ?? "",
        secret: integration.options?.token ?? "",
      });
    }
  }, [integration, reset]);

  const onSubmit = (data: TelegramFormValues) => {
    if (!integration) return;

    const variables = {
      input: {
        mrn: integration.mrn,
        name: integration.name,
        enabled: data.activeState,
        options: {
          ...integration.options,
          chatid: data.chatid,
          token: data.secret,
        },
      },
    };
    saveIntegration(variables);
  };

  if (!integration) {
    return <ChatOpsFormSkeleton height={402} lines={2} />;
  }

  return (
    <ChatOpsFormBase
      baseSubmit={handleSubmit(onSubmit)}
      isDirty={isDirty}
      isValid={isValid}
      hasPermission={hasChatOpsPermission}
    >
      <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Controller
          control={control}
          name="activeState"
          render={({ field }) => (
            <FormControlLabel
              labelPlacement="start"
              control={
                <Switch
                  color="primary"
                  checked={field.value}
                  onChange={field.onChange}
                />
              }
              label={field.value ? "Enabled" : "Disabled"}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name="chatid"
          rules={{ required: "Chat ID is required" }}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  Chat ID
                </Typography>
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                error={!!errors.chatid}
                helperText={errors.chatid?.message}
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name="secret"
          rules={{ required: "Secret is required" }}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  Secret
                </Typography>
              </FormLabel>
              <TextField
                {...field}
                type="password"
                fullWidth
                error={!!errors.secret}
                helperText={errors.secret?.message}
              />
            </FormControl>
          )}
        />
      </Grid>
    </ChatOpsFormBase>
  );
}
